'use client'

import { useEffect, useRef, useCallback } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuthStore } from '@/stores'
import { useValidateSession } from '@/services/auth/hooks'

const SESSION_EXPIRED_TOAST_KEY = 'dday-session-expired-toast'
const AUTH_STORAGE_KEY = 'dday-auth-storage'

// Hard ceiling on how long we'll wait for the auth bootstrap sequence.
// After this, we redirect to login (fail-closed). Must be shorter than
// any UX-visible spinner — users should never wait this long normally.
const AUTH_BOOTSTRAP_TIMEOUT_MS = 5000

/**
 * useRequireAuth
 *
 * Protects authenticated routes. Three possible outcomes on page load:
 *
 *   1. Already authenticated (localStorage hydrated with valid state)
 *      → show layout immediately, no loading at all.
 *
 *   2. Not authenticated, cookie may exist
 *      → run /auth/validate once, restore session if valid, else redirect.
 *
 *   3. Not authenticated, no cookie
 *      → redirect to login immediately.
 *
 * The auth store's `isLoading` flag is intentionally NOT used here.
 * That flag is for auth mutations (login/logout/token refresh) and is
 * managed by those flows. Background user-data fetches must not block
 * the layout from rendering for an already-authenticated user.
 */
export function useRequireAuth() {
  const { isAuthenticated, _hasHydrated, restoreSession, setHasHydrated } =
    useAuthStore()

  const redirectPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/invitations'
  const forcedLoginUrl = `/login?reauth=1&redirect=${encodeURIComponent(redirectPath)}`

  // Deduplicate redirects — once we've decided to redirect, don't do it again.
  const hasRedirectedRef = useRef(false)

  const forceRedirectToLogin = useCallback(() => {
    if (hasRedirectedRef.current) return
    hasRedirectedRef.current = true
    // Use document navigation as escape hatch: if the Next.js router is stuck
    // mid-transition this guarantees we break out of the deadlock.
    window.location.replace(forcedLoginUrl)
  }, [forcedLoginUrl])

  // Only run cookie validation when we're hydrated but not authenticated.
  // If the user IS authenticated we skip this entirely — no network call needed.
  const shouldValidate = _hasHydrated && !isAuthenticated

  const {
    data: sessionData,
    isLoading: isValidating,
    isError: validationFailed,
  } = useValidateSession(shouldValidate)

  // ── Hydration safety valve ────────────────────────────────────────────────
  // Zustand's onRehydrateStorage callback can fail silently (e.g. localStorage
  // quota exceeded, Safari ITP, corrupted JSON). If hydration hasn't completed
  // within a short window force it true so auth logic can proceed.
  useEffect(() => {
    if (_hasHydrated) return
    const t = window.setTimeout(() => setHasHydrated(true), 500)
    return () => window.clearTimeout(t)
  }, [_hasHydrated, setHasHydrated])

  // ── Session restore ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!shouldValidate || isValidating) return
    if (
      sessionData?.authenticated &&
      sessionData.data?.user &&
      sessionData.data?.accessToken
    ) {
      restoreSession(sessionData.data.user, sessionData.data.accessToken)
    }
  }, [shouldValidate, isValidating, sessionData, restoreSession])

  // ── Redirect when definitively unauthenticated ────────────────────────────
  useEffect(() => {
    if (!_hasHydrated) return
    if (shouldValidate && isValidating) return // still checking cookie
    if (validationFailed) return forceRedirectToLogin()
    if (sessionData?.authenticated) return // restore in progress
    if (!isAuthenticated) forceRedirectToLogin()
  }, [
    isAuthenticated,
    _hasHydrated,
    shouldValidate,
    isValidating,
    sessionData,
    validationFailed,
    forceRedirectToLogin,
  ])

  // ── Hard bootstrap timeout (fail-closed) ─────────────────────────────────
  // If the auth bootstrap sequence hasn't finished within the deadline,
  // redirect to login. Uses a monotonic start time so re-renders can't
  // extend the deadline.
  const bootstrapStartRef = useRef<number | null>(null)
  const isBootstrapping = !_hasHydrated || (shouldValidate && isValidating)

  useEffect(() => {
    if (!isBootstrapping) {
      bootstrapStartRef.current = null
      return
    }

    if (bootstrapStartRef.current === null) {
      bootstrapStartRef.current = Date.now()
    }

    const elapsed = Date.now() - bootstrapStartRef.current
    const remaining = Math.max(0, AUTH_BOOTSTRAP_TIMEOUT_MS - elapsed)

    const t = window.setTimeout(forceRedirectToLogin, remaining)
    return () => window.clearTimeout(t)
  }, [isBootstrapping, forceRedirectToLogin])

  // Show the loading overlay only while we're genuinely bootstrapping:
  // waiting for hydration or for the one-shot cookie validation.
  // An already-authenticated user on refresh sees isBootstrapping=false
  // immediately after hydration, so the overlay never appears for them.
  return { isAuthenticated, isLoading: isBootstrapping }
}

/**
 * useRedirectIfAuthenticated
 *
 * For public/auth pages (login, signup). Redirects authenticated users away.
 */
export function useRedirectIfAuthenticated(
  redirectTo: string = '/invitations'
) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore()
  // reauth=1 is set by proxy/session-expiry paths to prevent client-side
  // auto-redirect while the user is intentionally re-authenticating.
  const isForcedReauth = searchParams.get('reauth') === '1'
  const hasForcedReauthMarker =
    typeof window !== 'undefined' &&
    sessionStorage.getItem(SESSION_EXPIRED_TOAST_KEY) !== null
  // Either signal means "do not bounce this request away from login".
  const shouldBypassRedirect = hasForcedReauthMarker || isForcedReauth
  const shouldValidateRedirectSession =
    _hasHydrated && !isLoading && isAuthenticated && !shouldBypassRedirect

  const {
    data: redirectSessionData,
    isLoading: isValidatingRedirectSession,
    isError: redirectSessionValidationFailed,
  } = useValidateSession(shouldValidateRedirectSession)

  const hasValidRedirectSession = redirectSessionData?.authenticated === true
  // Source of truth for redirecting away from login is the cookie-backed
  // validation response, not persisted client auth.
  const hasInvalidRedirectSession =
    shouldValidateRedirectSession &&
    !isValidatingRedirectSession &&
    (redirectSessionValidationFailed ||
      redirectSessionData?.authenticated === false)
  const hasClearedStaleAuthRef = useRef(false)

  useEffect(() => {
    if (!hasInvalidRedirectSession || hasClearedStaleAuthRef.current) return

    // Clear stale local auth once so login can render instead of bounce-looping.
    hasClearedStaleAuthRef.current = true
    useAuthStore.setState({
      user: null,
      settings: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    })
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY)
    } catch {
      // Ignore storage errors (private mode, restricted storage, etc.)
    }
  }, [hasInvalidRedirectSession])

  useEffect(() => {
    if (!_hasHydrated) return
    if (isLoading) return
    if (shouldBypassRedirect) return
    if (!isAuthenticated) return
    if (isValidatingRedirectSession) return
    if (!hasValidRedirectSession) return
    router.push(redirectTo)
  }, [
    isAuthenticated,
    isLoading,
    _hasHydrated,
    shouldBypassRedirect,
    isValidatingRedirectSession,
    hasValidRedirectSession,
    router,
    redirectTo,
  ])

  // Keep loader visible while auth state is still being resolved for this route.
  const showLoading =
    !_hasHydrated ||
    isLoading ||
    (isAuthenticated && !shouldBypassRedirect && !hasInvalidRedirectSession)

  return { isAuthenticated, isLoading: showLoading }
}
